/**
 * Cookie consent banner.
 *
 * Shown only on the web platform (skipped on TG / LINE / Discord / Capacitor).
 * Detects EU/UK by timezone hint; for the privacy-first stance we surface it
 * regardless of region but offer a clean decline.
 *
 * Persisted via profile.cookieConsent. Banner removes itself once decided.
 */
import { t } from '../i18n/runtime';
import type { PlatformInfo } from '../platform/detect';
import { profile } from '../state/profile';
import { el, html, on } from './html';

const SHOULD_SHOW = (platform: PlatformInfo): boolean => {
  return platform.name === 'web' || platform.name === 'pwa';
};

export function maybeMountCookieBanner(platform: PlatformInfo): void {
  if (!SHOULD_SHOW(platform)) return;
  const p = profile().get();
  if (p.cookieConsent !== null) return;
  // Defer cookie banner until onboarding is complete — never block first-run UX.
  if (!p.onboarded) return;

  const root = el(html`
    <div class="cookie-banner" role="dialog" aria-label="${t('cookie.title')}">
      <div class="cookie-text">
        <strong>${t('cookie.title')}</strong>
        <p>${t('cookie.body')}</p>
      </div>
      <div class="cookie-actions">
        <button class="cta cta-ghost cookie-decline" data-decline>${t('cookie.decline')}</button>
        <button class="cta cta-primary cookie-accept" data-accept>${t('cookie.accept')}</button>
      </div>
    </div>
  `);
  on(root, 'click', '[data-accept]', () => {
    profile().set({ cookieConsent: 'accepted' });
    root.remove();
  });
  on(root, 'click', '[data-decline]', () => {
    profile().set({ cookieConsent: 'declined' });
    root.remove();
  });
  document.body.appendChild(root);
}
